import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import CursorFollower from "../components/ui/CursorFollower";


// 🧱 Layouts
import AppLayout from "../components/layouts/app/index";

// 💫 Loader
import Loading from "../components/Loading";
import AdminLayout from "../components/layouts/admindashboard";
import AuthLayout from "../components/layouts/auth/AuthLayout";
import RouteGuard from "../guards/authguard";
import { RoleGuard } from "../guards/RoleGuard";
import { AdminProvider } from "../context/AdminContext";
import { UserProvider } from "../context/UserContext";
import UserLayout from "../components/layouts/userdashboard";
import { ScrollToTop } from "../components/ui/ScrollToTop";
import AdminCommerceLayout from "../context/layouts/AdminCommerceLayout";
const UnauthorizedPage = lazy(() => import("../pages/UnauthorizedPage"));

/* =============================
   🔐 AUTH
============================= */
const LoginPage = lazy(() => import("../pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("../pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("../pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/auth/ResetPasswordPage"));
const VerifyCodePage = lazy(() => import("../pages/auth/VerifyCodePage"));
const NotificationsPage = lazy(() => import("../pages/userdashboard/notifications/index"));

/* =============================
   🌍 PAGES PUBLIQUES
============================= */
const HomePage = lazy(() => import("../pages/app/home"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const PublicProjectsPage = lazy(() => import("../pages/app/projects/index"));
const PublicProjectDetailPage = lazy(() => import("../pages/app/projects/show/inndex"));
const AccountDisabledPage = lazy(() => import("../pages/account/disabled/index"));
const ContactPage = lazy(() => import("../pages/app/contact/index"));
const PublicServicesPage = lazy(() => import("../pages/app/services/index"));
const PublicServicesDetailPage = lazy(() => import("../pages/app/services/show/index"));
const PublicPortfolioPage = lazy(() => import("../pages/app/portfolio/index"));
const PublicBoutiquePage = lazy(() => import("../pages/app/boutique/index"));
const PublicBoutiqueDetailPage = lazy(() => import("../pages/app/boutique/show/inndex"));
const DevisPage = lazy(() => import("../pages/app/devis/index"));


/* =============================
   🧭 PAGES DASHBOARD ADMIN
============================= */
const DashboardHomePage = lazy(() => import("../pages/admindashboard/home/index"));

// Contenu
const ProjectCategoriesPage = lazy(() => import("../pages/admindashboard/project-categories/index"));
const ProjectCategoryDetailPage = lazy(() => import("../pages/admindashboard/project-categories/show/inndex"));

const DashboardProjectsPage = lazy(() => import("../pages/admindashboard/projects/index"));
const ProjectDetailPage = lazy(() => import("../pages/admindashboard/projects/show/inndex"));

const ProductCategoriesPage = lazy(() => import("../pages/admindashboard/product-categories/index"));
const ProductCategoriesDetailPage = lazy(() => import("../pages/admindashboard/product-categories/show/inndex"));
const DashboardProductsPage = lazy(() => import("../pages/admindashboard/products/index"));
const DashboardProductsDetailPage = lazy(() => import("../pages/admindashboard/products/show/inndex"));
const DashboardServicesPage = lazy(() => import("../pages/admindashboard/services/index"));
const ServiceDetailPage = lazy(() => import("../pages/admindashboard/services/show/inndex"));

// Commerce
const DashboardOrdersPage = lazy(() => import("../pages/admindashboard/commerces/orders/index"));
const DashboardCustomersPage = lazy(() => import("../pages/admindashboard/commerces/customers/index"));
const DashboardProductStatsPage = lazy(() => import("../pages/admindashboard/commerces/product-stats/index"));

// finances
const DashboardTransactionsPage = lazy(() => import("../pages/admindashboard/finances/transactions/index"));
const DashboardReportsPage = lazy(() => import("../pages/admindashboard/finances/reports/index"));

// Communication
const ContactMessagesPage = lazy(() => import("../pages/admindashboard/contact-messages/index"));
//Devis
const DashboardAdminDevisPage = lazy(() => import("../pages/admindashboard/devis/index"));
// Administration
const DashboardAdminUsersPage = lazy(() => import("../pages/admindashboard/users/index"));
const AdminNotificationsPage = lazy(() => import("../pages/admindashboard/admin-notifications/index"));
const DashboardSettingsPage = lazy(() => import("../pages/admindashboard/settings/index"));
const PreferencesPage = lazy(() => import("../pages/admindashboard/preferences/index"));

// Compte
const ProfilePage = lazy(() => import("../pages/admindashboard/profile/index"));
const SecurityPage = lazy(() => import("../pages/admindashboard/security/index"));

// ===== DASHBOARD USER =====
// Principale
const UserDashboardHomePage = lazy(() => import("../pages/userdashboard/home/index"));
const UserOrdersPage = lazy(() => import("../pages/userdashboard/orders/index"));
const UserProductsPage = lazy(() => import("../pages/userdashboard/products/index"));

// Transactions & paiements
const UserPaymentsPage = lazy(() => import("../pages/userdashboard/payments/index"));

// Mon compte
const UserProfilePage = lazy(() => import("../pages/userdashboard/profile/index"));
const UserSecurityPage = lazy(() => import("../pages/userdashboard/security/index"));
const UserPreferencesPage = lazy(() => import("../pages/userdashboard/preferences/index"));
const UserSettingsPage = lazy(() => import("../pages/userdashboard/settings/index"));

// Notifications & activité
const UserNotificationsPage = lazy(() => import("../pages/userdashboard/notifications/index"));

const CheckoutPage = lazy(() => import("../pages/app/checkout/CheckoutPage"));


/* =============================
   🚀 ROUTEUR PRINCIPAL
============================= */
const AppRouter: React.FC = () => {
  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* ===================== Authentification ===================== */}

          <Route element={<RouteGuard isAuth />}>
            <Route element={<AuthLayout />}>
              <Route path="/auth/login" element={<Suspense fallback={<Loading />}><LoginPage /></Suspense>} />
              <Route path="/auth/register" element={<Suspense fallback={<Loading />}><RegisterPage /></Suspense>} />
              <Route path="/auth/forgot-password" element={<Suspense fallback={<Loading />}><ForgotPasswordPage /></Suspense>} />
              <Route path="/auth/reset-password" element={<Suspense fallback={<Loading />}><ResetPasswordPage /></Suspense>} />
              <Route path="/auth/verify-code" element={<Suspense fallback={<Loading />}><VerifyCodePage /></Suspense>} />
            </Route>
          </Route>


          {/* ===================== PUBLIC===================== */}
          <Route element={<AppLayout />}>
            <Route path="/" element={<Suspense fallback={<Loading />}><HomePage /></Suspense>} />
            <Route path="/projects" element={<Suspense fallback={<Loading />}><PublicProjectsPage /></Suspense>} />
            <Route path="/projects/:id" element={<Suspense fallback={<Loading />}><PublicProjectDetailPage /></Suspense>} />
            <Route path="/account-disabled" element={<Suspense fallback={<Loading />}><AccountDisabledPage /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<Loading />}><ContactPage /></Suspense>} />
            <Route path="/services" element={<Suspense fallback={<Loading />}><PublicServicesPage /></Suspense>} />
            <Route path="/services/:id" element={<Suspense fallback={<Loading />}><PublicServicesDetailPage /></Suspense>} />
            <Route path="/portfolio" element={<Suspense fallback={<Loading />}><PublicPortfolioPage /></Suspense>} />
            <Route path="/boutique" element={<Suspense fallback={<Loading />}><PublicBoutiquePage /></Suspense>} />
            <Route path="/boutique/:id" element={<Suspense fallback={<Loading />}><PublicBoutiqueDetailPage /></Suspense>} />
            <Route path="/devis" element={<Suspense fallback={<Loading />}><DevisPage /></Suspense>} />

          </Route>


          {/* ===================== DASHBOARD USER ===================== */}
          <Route element={<RouteGuard isPrivate />}>
            <Route element={<RoleGuard roles={["user"]} />}>

              <Route element={<AppLayout />}>
                <Route
                  path="/checkout"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CheckoutPage />
                    </Suspense>
                  }
                />
              </Route>

              <Route element={<UserProvider><UserLayout /></UserProvider>}>

                {/* Principale */}
                <Route path="/user/dashboard/home" element={<Suspense fallback={<Loading />}><UserDashboardHomePage /></Suspense>} />

                <Route path="/user/dashboard/orders" element={<Suspense fallback={<Loading />}><UserOrdersPage /></Suspense>} />
                <Route path="/user/dashboard/products" element={<Suspense fallback={<Loading />}><UserProductsPage /></Suspense>} />

                {/* Transactions & paiements */}
                <Route path="/user/dashboard/payments" element={<Suspense fallback={<Loading />}><UserPaymentsPage /></Suspense>} />

                {/* Mon compte */}
                <Route path="/user/dashboard/profile" element={<Suspense fallback={<Loading />}><UserProfilePage /></Suspense>} />
                <Route path="/user/dashboard/security" element={<Suspense fallback={<Loading />}><UserSecurityPage /></Suspense>} />
                <Route path="/user/dashboard/preferences" element={<Suspense fallback={<Loading />}><UserPreferencesPage /></Suspense>} />
                <Route path="/user/dashboard/settings" element={<Suspense fallback={<Loading />}><UserSettingsPage /></Suspense>} />

                {/* Notifications & activité */}
                <Route path="/user/dashboard/notifications" element={<Suspense fallback={<Loading />}><UserNotificationsPage /></Suspense>} />
                <Route path="/user/dashboard/notifications" element={<Suspense fallback={<Loading />}><UserNotificationsPage /></Suspense>} />

              </Route>
            </Route>
          </Route>


          {/* ===================== DASHBOARD ADMIN ===================== */}
          <Route element={<RouteGuard isPrivate />}>
            <Route element={<RoleGuard roles={["admin", "platform_admin"]} />}>
              <Route
                element={
                  <AdminProvider>
                    <AdminLayout />
                  </AdminProvider>
                }
              >

                {/* Contenu */}
                <Route path="/admin/dashboard/project-categories" element={<Suspense fallback={<Loading />}><ProjectCategoriesPage /></Suspense>} />
                <Route path="/admin/dashboard/project-categories/:id" element={<Suspense fallback={<Loading />}><ProjectCategoryDetailPage /></Suspense>} />
                <Route path="/admin/dashboard/projects" element={<Suspense fallback={<Loading />}><DashboardProjectsPage /></Suspense>} />
                <Route path="/admin/dashboard/projects/:id" element={<Suspense fallback={<Loading />}><ProjectDetailPage /></Suspense>} />
                <Route path="/admin/dashboard/product-categories" element={<Suspense fallback={<Loading />}><ProductCategoriesPage /></Suspense>} />
                <Route path="/admin/dashboard/product-categories/:id" element={<Suspense fallback={<Loading />}><ProductCategoriesDetailPage /></Suspense>} />
                <Route path="/admin/dashboard/products" element={<Suspense fallback={<Loading />}><DashboardProductsPage /></Suspense>} />
                <Route path="/admin/dashboard/products/:id" element={<Suspense fallback={<Loading />}><DashboardProductsDetailPage /></Suspense>} />
                <Route path="/admin/dashboard/services" element={<Suspense fallback={<Loading />}><DashboardServicesPage /></Suspense>} />
                <Route path="/admin/dashboard/services/:id" element={<Suspense fallback={<Loading />}><ServiceDetailPage /></Suspense>} />
                {/* Commerce */}
                <Route element={<AdminCommerceLayout />}>
                  {/* Principale */}
                  <Route path="/admin/dashboard/home" element={<Suspense fallback={<Loading />}><DashboardHomePage /></Suspense>} />
                  <Route path="/admin/dashboard/commerces/orders" element={<Suspense fallback={<Loading />}><DashboardOrdersPage /></Suspense>} />
                  <Route path="/admin/dashboard/commerces/customers" element={<Suspense fallback={<Loading />}><DashboardCustomersPage /></Suspense>} />
                  <Route path="/admin/dashboard/finances/transactions" element={<Suspense fallback={<Loading />}><DashboardTransactionsPage /></Suspense>} />
                  <Route path="/admin/dashboard/finances/reports" element={<Suspense fallback={<Loading />}><DashboardReportsPage /></Suspense>} />
                  <Route path="/admin/dashboard/commerces/product-stats" element={<Suspense fallback={<Loading />}><DashboardProductStatsPage /></Suspense>} />
                </Route>

                {/* Communication */}
                <Route path="/admin/dashboard/contact-messages" element={<Suspense fallback={<Loading />}><ContactMessagesPage /></Suspense>} />
                {/* Devis */}
                <Route path="/admin/dashboard/devis-requests" element={<Suspense fallback={<Loading />}><DashboardAdminDevisPage /></Suspense>} />
                {/* Administration */}
                <Route path="/admin/dashboard/users" element={<Suspense fallback={<Loading />}><DashboardAdminUsersPage /></Suspense>} />
                <Route path="/admin/dashboard/admin-notifications" element={<Suspense fallback={<Loading />}><AdminNotificationsPage /></Suspense>} />
                <Route path="/admin/dashboard/preferences" element={<Suspense fallback={<Loading />}><PreferencesPage /></Suspense>} />
                <Route path="/admin/dashboard/settings" element={<Suspense fallback={<Loading />}><DashboardSettingsPage /></Suspense>} />

                {/* Compte */}
                <Route path="/admin/dashboard/profile" element={<Suspense fallback={<Loading />}><ProfilePage /></Suspense>} />
                <Route path="/admin/dashboard/security" element={<Suspense fallback={<Loading />}><SecurityPage /></Suspense>} />

                <Route path="/admin/dashboard/notifications" element={<Suspense fallback={<Loading />}><NotificationsPage /></Suspense>} />


              </Route>
            </Route>
          </Route>

          {/* ===================== 404 ===================== */}
          <Route path="*" element={<Suspense fallback={<Loading />}><NotFoundPage /></Suspense>} />
          <Route path="/unauthorized" element={<Suspense fallback={<Loading />}><UnauthorizedPage /></Suspense>} />
        </Routes>
      </BrowserRouter >
      <CursorFollower />

    </ >
  );
};

export default AppRouter;
